import { cn } from '@/lib/utils';

interface PlaceholderCardProps {
    /**
     * The primary line. Either the bold card title (snapshot states) or the
     * single muted line (preview panes) — `titleClassName` controls which.
     */
    title: React.ReactNode;
    /** Optional secondary line rendered below the title. */
    description?: React.ReactNode;
    /** Optional leading glyph (e.g. a lucide icon), rendered above the title. */
    icon?: React.ReactNode;
    /** Wrapper classes — lets each caller keep its exact layout/spacing. */
    className?: string;
    /** Classes for the title `<p>` — callers vary between bold and muted. */
    titleClassName?: string;
    /** Classes for the description `<p>`. */
    descriptionClassName?: string;
    'data-testid'?: string;
    /** Extra content rendered after the description (rare). */
    children?: React.ReactNode;
}

/**
 * A quiet "nothing to show here" placeholder used across the GT hub.
 *
 * Pure DRY extraction of two previously-duplicated shapes:
 *   - the centered icon + message panes in `GroundTruthPreview`
 *   - the dashed-border state cards in `SnapshotPanel`
 *
 * It stays structurally minimal (optional icon → title → optional description →
 * children) and lets callers pass through their own wrapper/title classes so
 * the rendered output is byte-for-byte identical to what each replaced.
 */
export function PlaceholderCard({
    title,
    description,
    icon,
    className,
    titleClassName,
    descriptionClassName,
    'data-testid': dataTestId,
    children,
}: PlaceholderCardProps) {
    return (
        <div className={cn(className)} data-testid={dataTestId}>
            {icon}
            <p className={titleClassName}>{title}</p>
            {description !== undefined && (
                <p className={descriptionClassName}>{description}</p>
            )}
            {children}
        </div>
    );
}
